import React, { useState, useEffect } from 'react';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import { FileDown, ThumbsUp, ThumbsDown, History } from 'lucide-react';

import './App.css';
import Report from './components/Report';
import HistorySidebar from './components/HistorySidebar';
import ChatWindow from './components/ChatWindow';

function App() {
  const [idea, setIdea] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const savedHistory = localStorage.getItem('validationHistory');
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, []);

  const saveToHistory = (newReport) => {
    const newHistory = [newReport, ...history].slice(0, 10); // Keep last 10
    setHistory(newHistory);
    localStorage.setItem('validationHistory', JSON.stringify(newHistory));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!idea.trim()) {
      setError('Please enter a startup idea.');
      return;
    }
    setLoading(true);
    setError('');
    setReport(null);
    try {
      const response = await axios.post('https://start-up-idea-validator-polpewjq2-salahs-projects-e5953328.vercel.app/api/validate', { idea });
      const newReport = { id: Date.now(), idea, ...response.data };
      setReport(newReport);
      saveToHistory(newReport);
      setFeedback(null); // Reset feedback on new report
    } catch (err) {
      setError(err.response?.data?.error || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    const element = document.getElementById('report-content');
    const opt = {
      margin:       0.5,
      filename:     `${idea.replace(/\s+/g, '_')}_validation_report.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true },
      jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };
  
  const loadFromHistory = (pastReport) => {
    setIdea(pastReport.idea);
    setReport(pastReport);
    setShowHistory(false);
  }

  return (
    <div className="App">
      <HistorySidebar 
        history={history} 
        onLoad={loadFromHistory}
        onClear={() => { setHistory([]); localStorage.removeItem('validationHistory'); }}
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
      />
      
      <header className="App-header">
        <button onClick={() => setShowHistory(true)} className="history-btn">
          <History size={20} /> View History
        </button>
        <h1>🚀 Startup Idea Validator</h1>
        <p>From napkin sketch to investment-ready analysis. Instantly.</p>
      </header>

      <main>
        <div className="idea-form-container">
          <form onSubmit={handleSubmit} className="idea-form">
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              placeholder="e.g., An AI-powered personal tutor for high school students"
              rows="3"
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Analyzing...' : 'Validate My Idea'}
            </button>
          </form>
          {error && <p className="error-message">{error}</p>}
        </div>

        {loading && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Our AI is brewing up some insights. This might take a moment...</p>
          </div>
        )}

        {report && (
          <div id="report-container">
            <div className="report-actions">
              <button onClick={handleExportPDF}><FileDown size={16} /> Export as PDF</button>
            </div>
            <div id="report-content">
              <Report report={report} idea={idea} />
            </div>
            <ChatWindow report={report} />
            <div className="feedback-section">
              <span>Was this validation useful?</span>
              <div className="feedback-buttons">
                <button 
                  onClick={() => setFeedback('up')}
                  className={feedback === 'up' ? 'active' : ''}
                >
                  <ThumbsUp size={16} /> Yes
                </button>
                <button 
                  onClick={() => setFeedback('down')}
                  className={feedback === 'down' ? 'active' : ''}
                >
                  <ThumbsDown size={16} /> No
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
